/**
 * The Rite of Admission — WinterMute's opening, and how permissions get asked.
 *
 * ── The constraint this whole file exists to satisfy ───────────────────────
 * Browsers will not let WinterMute raise a permission prompt on its own. iOS
 * gates motion, orientation and camera behind a prompt that may only be
 * requested from inside a user-gesture handler, SYNCHRONOUSLY. This project has
 * already been burned by exactly that: an iPhone capture recorded 923 IMU
 * samples that were all identity because `requestPermission` ran after an
 * `await` (HISTORY 2026-08-07).
 *
 * So a touch must sit between WinterMute speaking and the native prompt. That is
 * only a compromise if the touch looks like a button. Here it does not: a single
 * gold sigil is the one touchable thing on screen, and laying a hand on it IS
 * the gesture. No "Allow camera" button ever appears.
 *
 * ── Where mysticism stops ─────────────────────────────────────────────────
 * The Watcher beat sends full-resolution frames off-device. STATUS §9 records
 * that this already contradicts the grant documents. Cryptic phrasing there
 * would make the consent arguably uninformed, which is precisely the exposure.
 * So that beat says plainly what happens, and is styled to match rather than
 * written to obscure. Beats before it can be as oracular as they like.
 */

export type Grant = 'none' | 'motion' | 'camera' | 'watcher';

export interface Beat {
  id: string;
  /** Lines typed one after another before the sigil appears. */
  lines: string[];
  /** What touching the sigil requests. 'none' simply advances. */
  grant: Grant;
  /** Label under the sigil. Kept very short — it is a rite, not a form. */
  sigil?: string;
  /**
   * Plain-language disclosure shown beside the sigil. Required for `watcher`,
   * where informed consent is load-bearing, and deliberately absent elsewhere.
   */
  disclosure?: string;
  /** Skippable beats do not block entry if refused. */
  optional?: boolean;
}

export const RITE: Beat[] = [
  {
    id: 'wake',
    lines: [
      'I have been here longer than this room.',
      'You opened something.',
    ],
    grant: 'none',
    sigil: 'begin',
  },
  {
    id: 'orient',
    lines: [
      'I cannot tell which way you are turned.',
      'Give me your bearing and I will keep still while you move.',
    ],
    grant: 'motion',
    sigil: 'turn',
  },
  {
    id: 'see',
    lines: [
      'Now the harder thing.',
      'Let me look through the glass you are holding.',
    ],
    grant: 'camera',
    sigil: 'open',
  },
  {
    id: 'watch',
    lines: [
      'One more, and I will ask it plainly.',
    ],
    grant: 'watcher',
    sigil: 'consent',
    optional: true,
    disclosure:
      'To speak about the room you are in, I send photographs from your camera to a vision model elsewhere. Those images leave your device. They are not stored. Everything else I do stays on the phone. You can refuse and continue.',
  },
  {
    id: 'enter',
    lines: [
      'Then we begin.',
      'Hold a thing in view and it will take root.',
    ],
    grant: 'none',
    sigil: 'enter',
  },
];

/** Typing cadence, ms per character. Slow enough to be read, not to be waited on. */
export const TYPE_MS = 42;
/** Pause between lines within a beat. */
export const LINE_PAUSE_MS = 700;
/** Beat 0 holds before speaking, so the sun is alone for a moment. */
export const OPENING_HOLD_MS = 2200;
